import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiCall } from '@/utils/api';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  EyeOff,
  Tag,
  ChevronDown,
  ChevronUp,
  X,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface TagData {
  tag_name: string;
  lead_count: number;
  won_count: number;
}

interface TagsResponse {
  all_tags: TagData[];
  tags_per_month: Array<{ month: string; tag_name: string; count: number }>;
  source_tags_config: Record<string, string>;
}

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#ef4444', '#06b6d4', '#84cc16', '#f97316'];

const DEFAULT_VISIBLE_TAGS = 10;

const KommoTags: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<TagsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [excludedTags, setExcludedTags] = useState<Set<string>>(new Set());
  const [showExcludePanel, setShowExcludePanel] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await apiCall<{ success: boolean; data: TagsResponse }>(token, '/kommo/analytics/tags');
        if (res.success) setData(res.data);
      } catch (err) {
        toast.error('Erreur lors du chargement des tags');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const toggleTag = (tagName: string) => {
    setExcludedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tagName)) {
        next.delete(tagName);
      } else {
        next.add(tagName);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
      </div>
    );
  }

  if (!data) return null;

  const visibleTags = data.all_tags.filter((t) => !excludedTags.has(t.tag_name));
  const displayedTags = showAllTags ? visibleTags : visibleTags.slice(0, DEFAULT_VISIBLE_TAGS);
  const hasMoreTags = visibleTags.length > DEFAULT_VISIBLE_TAGS;
  const totalLeads = visibleTags.reduce((sum, t) => sum + t.lead_count, 0);

  // Build time series data for source tags only
  const timeSeriesMap = new Map<string, Record<string, number>>();
  const sourceTags = Object.keys(data.source_tags_config || {});

  for (const entry of data.tags_per_month) {
    if (!timeSeriesMap.has(entry.month)) {
      timeSeriesMap.set(entry.month, { month: entry.month } as any);
    }
    const row = timeSeriesMap.get(entry.month)!;
    row[entry.tag_name] = entry.count;
  }

  const timeSeriesData = Array.from(timeSeriesMap.values()).sort(
    (a, b) => String(a.month).localeCompare(String(b.month))
  );

  return (
    <div className="px-4 py-4 space-y-6">
      {/* Header */}
      <div className="bg-white p-5 rounded-xl border border-slate-200">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
          Analyse des Tags
        </h1>
        <p className="text-sm text-slate-500 font-medium mt-0.5">
          {data.all_tags.length} tags uniques detectes
        </p>
      </div>

      {/* Tag breakdown - Cards with exclude panel */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-bold text-slate-900">Repartition par tag</CardTitle>
              <CardDescription className="text-xs text-slate-500">
                {visibleTags.length} tags affiches sur {data.all_tags.length}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {excludedTags.size > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setExcludedTags(new Set())}>
                  Tout afficher
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setShowExcludePanel(!showExcludePanel)}
              >
                <EyeOff className="h-3.5 w-3.5" />
                Masquer des tags
                {excludedTags.size > 0 && (
                  <Badge variant="secondary" className="ml-1 text-[10px] px-1.5">
                    {excludedTags.size}
                  </Badge>
                )}
                {showExcludePanel ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </Button>
            </div>
          </div>

          {/* Exclude panel */}
          {showExcludePanel && (
            <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
              <p className="text-xs font-medium text-slate-500 mb-2">Cochez les tags a masquer :</p>
              <div className="max-h-48 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-1">
                {data.all_tags.map((tag) => (
                  <label
                    key={tag.tag_name}
                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white cursor-pointer text-sm"
                  >
                    <Checkbox
                      checked={excludedTags.has(tag.tag_name)}
                      onCheckedChange={() => toggleTag(tag.tag_name)}
                    />
                    <span className="truncate text-xs">{tag.tag_name}</span>
                    <span className="text-[10px] text-slate-400 font-mono ml-auto">
                      {tag.lead_count.toLocaleString('fr-FR')}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Excluded tags badges */}
          {excludedTags.size > 0 && !showExcludePanel && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {Array.from(excludedTags).map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="text-[10px] cursor-pointer hover:bg-slate-200 gap-1"
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                  <X className="h-2.5 w-2.5" />
                </Badge>
              ))}
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {displayedTags.map((tag, i) => {
              const pct = totalLeads > 0 ? (tag.lead_count / totalLeads) * 100 : 0;
              const color = COLORS[i % COLORS.length];
              return (
                <div
                  key={tag.tag_name}
                  className="p-3 rounded-lg border border-slate-100 bg-slate-50/50 hover:bg-white hover:shadow-sm transition-all"
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <Tag className="h-3 w-3" style={{ color }} />
                    <span className="text-xs font-medium text-slate-700 truncate">{tag.tag_name}</span>
                  </div>
                  <div className="text-lg font-bold text-slate-900">
                    {tag.lead_count.toLocaleString('fr-FR')}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-slate-400 font-medium">{pct.toFixed(1)}%</span>
                    {tag.won_count > 0 && (
                      <span className="text-[10px] text-emerald-600 font-semibold">
                        {tag.won_count} conv. ({tag.lead_count > 0 ? ((tag.won_count / tag.lead_count) * 100).toFixed(1) : 0}%)
                      </span>
                    )}
                  </div>
                  <div className="h-1 bg-slate-100 rounded-full mt-2 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${pct}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {hasMoreTags && (
            <div className="mt-4 text-center">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setShowAllTags(!showAllTags)}
              >
                {showAllTags ? (
                  <>Afficher moins <ChevronUp className="h-3 w-3" /></>
                ) : (
                  <>Afficher plus de tags ({visibleTags.length - DEFAULT_VISIBLE_TAGS} restants) <ChevronDown className="h-3 w-3" /></>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tags per month time series */}
      {timeSeriesData.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-bold text-slate-900">Evolution des tags source par mois</CardTitle>
            <CardDescription className="text-xs text-slate-500">12 derniers mois</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                  <Legend />
                  {sourceTags.map((tagName, i) => (
                    <Line
                      key={tagName}
                      type="monotone"
                      dataKey={tagName}
                      stroke={COLORS[i]}
                      strokeWidth={1.5}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default KommoTags;
